/**
 * The answer, in full: why it stands where it does, and how it got there.
 *
 * TWO SIZES, like the room brief. The line at the front of the room is the
 * stance and how strongly it is held. Everything behind it — the reasons, the
 * counter-case, what it rubbed out, every version — used to open UNDER that line
 * and push the tables it rests on off the bottom of the screen, at exactly the
 * moment the reader wanted to hold the two side by side. A slideout sits over
 * the page instead, and the tables keep their height.
 *
 * Its own slideout rather than a card in the room brief: the brief is the
 * PROCESS (what the room is doing, how to steer it) and this is the RESULT, and
 * the history below would double the brief's length and bury the steering. The
 * two are joined where they genuinely meet — "what would change it" stages the
 * next dig.
 */
import { useState, type ReactNode } from 'react';
import type { BoardroomAnswer, BoardroomAnswerClaim, BoardroomAnswering } from '../../../types';
import { Card } from './DirectorNote';
import {
  answererNote,
  answerTimeline,
  boardWords,
  cappedNote,
  isReversal,
  movementLine,
  movementWords,
  sides,
  strengthTone,
  strengthWords,
  subjectWords,
  type VersionEntry,
} from './answerStance';

const H = 'mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400';

const when = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

function Strength({ value }: { value: string }) {
  return <span className={`rounded-full px-1.5 py-px text-xs ${strengthTone(value)}`}>{strengthWords(value)}</span>;
}

function Bullets({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <ul className="flex flex-col gap-1" data-testid={testId}>
      {children}
    </ul>
  );
}

function Bullet({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-2">
      <span className="text-gray-300">·</span>
      <span className="min-w-0">{children}</span>
    </li>
  );
}

function Claim({ c }: { c: BoardroomAnswerClaim }) {
  return (
    <Bullet>
      {c.text}
      {/* A COUNT, not item numbers: "#5182" means nothing to a reader. */}
      {c.receipt_ids.length > 0 && (
        <span className="ml-1 text-[11px] text-gray-400">
          · {c.receipt_ids.length} source{c.receipt_ids.length === 1 ? '' : 's'}
        </span>
      )}
    </Bullet>
  );
}

export default function AnswerBrief({
  answer,
  history,
  answering,
  turnsDone,
  onUse,
  onDismiss,
  onDig,
  part = 'all',
}: {
  /** "now": the answer as it stands. "history": its earlier versions only -
   *  the room tab folds them away under "How it got here". */
  part?: 'now' | 'history' | 'all';
  answer: BoardroomAnswer | null;
  history: BoardroomAnswer[];
  answering?: BoardroomAnswering;
  turnsDone: number;
  onUse: (answerId: number) => Promise<void>;
  onDismiss: (answerId: number, reason?: string) => Promise<void>;
  /** Stage a dig at one of the things that would change the answer. */
  onDig?: (directive: string) => void;
}) {
  // One act at a time: a second click on "use this answer" while the first is
  // in flight would write a second restore row.
  const [busy, setBusy] = useState(false);
  const act = (fn: (answerId: number) => Promise<void>) => async (answerId: number) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn(answerId);
    } finally {
      setBusy(false);
    }
  };
  const dismiss = async (answerId: number, reason?: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await onDismiss(answerId, reason);
    } finally {
      setBusy(false);
    }
  };
  const parked = answererNote(answering);
  const { entries, truncated } = answerTimeline(history, answer?.id);
  const versions = entries.filter((e): e is VersionEntry => e.kind === 'version').length;
  const checks = entries.reduce((n, e) => n + (e.kind === 'checked' ? e.count : 0), 0);

  const showNow = part !== 'history';
  const showHistory = part !== 'now';
  if (part === 'history' && entries.length === 0) return null;

  return (
    <div className="flex flex-col gap-3" data-testid={part === 'history' ? 'answer-history' : 'answer-brief'}>
      {showNow && (
      <>
      {/* WHY IT IS NOT ANSWERING, first — a parked answerer looked exactly like
          one that had not started, because nothing read the reason. */}
      {parked && (
        <div
          className={`rounded-lg border px-3 py-2 text-xs ${
            parked.tone === 'bad'
              ? 'border-amber-300 bg-amber-50/60 text-amber-800 dark:border-amber-700 dark:bg-amber-900/10 dark:text-amber-200'
              : 'border-gray-200 bg-white text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
          }`}
          data-testid="answer-parked"
        >
          {parked.text}
        </div>
      )}

      {answer ? <Now answer={answer} turnsDone={turnsDone} answering={answering} busy={busy} onDismiss={dismiss} onDig={onDig} /> : (
        <Card title="Now">
          <div className="text-xs text-gray-400 italic">no answer yet · the room answers its own question once a few findings land</div>
        </Card>
      )}
      </>
      )}

      {showHistory && entries.length > 0 && (
        <Card
          title="How it got here"
          aside={`${versions} version${versions === 1 ? '' : 's'}${checks ? ` · checked ${checks} time${checks === 1 ? '' : 's'}` : ''}`}
          testId="answer-timeline"
        >
          <ol className="flex flex-col">
            {entries.map((e, i) =>
              e.kind === 'checked' ? (
                <li
                  key={`checked-${i}`}
                  className="ml-3 border-l border-dashed border-gray-200 py-1 pl-4 text-xs text-gray-400 dark:border-gray-700"
                  data-testid="answer-checked"
                >
                  checked{e.count > 1 ? ` ${e.count} times` : ''}, still stood
                  {e.lookups > 0 && ` · on ${e.lookups} lookup${e.lookups === 1 ? '' : 's'}`}
                </li>
              ) : (
                <Version key={e.answer.id} e={e} busy={busy} onUse={act(onUse)} />
              ),
            )}
          </ol>
          {truncated && <div className="mt-2 text-xs text-gray-400">older versions not shown</div>}
        </Card>
      )}
    </div>
  );
}

/** Where the answer stands, and everything it rests on. */
function Now({
  answer,
  turnsDone,
  answering,
  busy,
  onDismiss,
  onDig,
}: {
  answer: BoardroomAnswer;
  turnsDone: number;
  answering?: BoardroomAnswering;
  busy: boolean;
  onDismiss: (answerId: number, reason?: string) => void;
  onDig?: (directive: string) => void;
}) {
  const split = sides(answer);
  const capped = cappedNote(answer);
  const board = boardWords(answer);
  const subject = subjectWords(answer);

  return (
    <Card
      title="Now"
      aside={<Strength value={answer.strength} />}
      tone={isReversal(answer) ? 'attention' : 'plain'}
      testId="answer-now"
    >
      <div className="flex flex-col gap-3 text-xs text-gray-700 dark:text-gray-200">
        <div>
          <div className="text-sm font-medium leading-snug text-gray-900 dark:text-gray-100" data-testid="answer-brief-stance">
            {answer.stance}
          </div>
          {subject && (
            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400" data-testid="answer-subject">
              {subject}
            </div>
          )}
          <div className="mt-1 text-xs text-gray-400">
            {movementLine(answer, turnsDone, { working: answering?.working })}
            {answer.question && <span title={answer.question}> · answering “{answer.question}”</span>}
          </div>
          {answer.moved_by && <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{answer.moved_by}</div>}
        </div>

        {/* THE HONESTY, IN FULL. The line has room for one of these; here both
            fit — why it is not stronger, and every count behind it. */}
        {(capped || board || (answer.strength_asked && answer.strength_asked !== answer.strength)) && (
          <div className="flex flex-col gap-0.5 text-xs text-gray-500 dark:text-gray-400" data-testid="answer-board">
            {capped && <div>{capped}</div>}
            {board && <div>{board}</div>}
            {answer.strength_asked && answer.strength_asked !== answer.strength && (
              <div data-testid="answer-counted">
                the room asked for {strengthWords(answer.strength_asked)}; the board supports {strengthWords(answer.strength)}
              </div>
            )}
          </div>
        )}

        {isReversal(answer) && answer.ruled_out.length > 0 && (
          <div>
            <div className={H}>changed from</div>
            <Bullets testId="answer-brief-ruled-out">
              {answer.ruled_out.map(r => (
                <Bullet key={r.stance_key}>
                  <span className="line-through opacity-70">{r.stance || 'the answer before this one'}</span>
                  {r.why && <span className="text-gray-600 dark:text-gray-300"> - {r.why}</span>}
                </Bullet>
              ))}
            </Bullets>
          </div>
        )}

        {split.for.length > 0 && (
          <div>
            <div className={H}>because</div>
            <Bullets testId="answer-because">
              {split.for.map(c => (
                <Claim key={c.key} c={c} />
              ))}
            </Bullets>
          </div>
        )}
        {/* THE COUNTER-CASE, BELOW A LINE. An answer that lists what argues
            against it is doing the thing that earns `confident`. */}
        {split.against.length > 0 && (
          <div className="border-t border-gray-200 pt-2 dark:border-gray-700">
            <div className={H}>against it</div>
            <Bullets testId="answer-against">
              {split.against.map(c => (
                <Claim key={c.key} c={c} />
              ))}
            </Bullets>
          </div>
        )}
        {/* WHAT IT RUBBED OUT: struck through, WITH the reason — a struck line
            with no reason is just a thing that disappeared. */}
        {answer.dropped.length > 0 && (
          <div>
            <div className={H}>stopped saying</div>
            <Bullets testId="answer-dropped">
              {answer.dropped.map(d => (
                <Bullet key={d.key}>
                  <span className="line-through text-gray-400">{d.text}</span>
                  {d.why && <span className="text-gray-500 dark:text-gray-400"> - {d.why}</span>}
                </Bullet>
              ))}
            </Bullets>
          </div>
        )}
        {/* WHERE THE TWO SLIDEOUTS MEET. What would change the answer is a dig
            waiting to be asked for, so it can be staged from here. */}
        {answer.would_change_it.length > 0 && (
          <div>
            <div className={H}>what would change it</div>
            <Bullets testId="answer-would-change">
              {answer.would_change_it.map((w, i) => (
                <Bullet key={`${w.text}-${i}`}>
                  {w.text}
                  {onDig && (
                    <button
                      type="button"
                      onClick={() => onDig(w.text)}
                      className="ml-2 text-xs text-indigo-600 hover:underline dark:text-indigo-300"
                    >
                      dig into this
                    </button>
                  )}
                </Bullet>
              ))}
            </Bullets>
          </div>
        )}

        <DismissAnswer busy={busy} onDismiss={reason => onDismiss(answer.id, reason)} />
      </div>
    </Card>
  );
}

/** DISMISSING ASKS FIRST. One click used to swap the room's answer - and the
 *  shared page's with it - with nothing said. Now it says what will happen and
 *  takes an optional reason, which the next answer pass is shown so it does
 *  not just say the same thing again. */
function DismissAnswer({ busy, onDismiss }: { busy: boolean; onDismiss: (reason?: string) => void }) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  if (!asking) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setAsking(true)}
          disabled={busy}
          className="rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700"
          data-testid="answer-dismiss"
        >
          Dismiss this answer
        </button>
      </div>
    );
  }
  return (
    <form
      className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50/60 p-2.5 text-xs dark:border-amber-700 dark:bg-amber-900/15"
      onSubmit={e => {
        e.preventDefault();
        onDismiss(reason.trim() || undefined);
        setAsking(false);
        setReason('');
      }}
      data-testid="answer-dismiss-confirm"
    >
      <p className="text-amber-900 dark:text-amber-100">
        Dismiss this answer? The earlier answer comes back, here and on any shared page. You can bring this
        one back from How it got here.
      </p>
      <input
        autoFocus
        value={reason}
        onChange={e => setReason(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Escape') setAsking(false);
        }}
        placeholder="Why? (optional - the room reads this before it answers again)"
        className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        data-testid="answer-dismiss-reason"
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          className="rounded bg-amber-600 px-2 py-0.5 text-white hover:bg-amber-700 disabled:opacity-50"
        >
          Dismiss
        </button>
        <button type="button" onClick={() => setAsking(false)} className="px-1 text-gray-500 hover:underline">
          Cancel
        </button>
      </div>
    </form>
  );
}

/** One version on the timeline: what it did, what moved it, what it changed. */
function Version({ e, busy, onUse }: { e: VersionEntry; busy: boolean; onUse: (answerId: number) => void }) {
  const a = e.answer;
  const reversed = isReversal(a);
  // What it stopped saying: the answerer's own account where it gave one —
  // that carries the reason — and the mechanical diff where it did not.
  const gone = a.dropped.length ? a.dropped.map(d => ({ text: d.text, why: d.why })) : e.diff.gone.map(text => ({ text, why: '' }));

  return (
    <li
      className={`relative ml-3 border-l py-2 pl-4 ${reversed ? 'border-amber-300 dark:border-amber-700' : 'border-gray-200 dark:border-gray-700'}`}
      data-testid={`answer-version-${a.id}`}
    >
      <span
        className={`absolute -left-[5px] top-3 h-2 w-2 rounded-full ${
          e.current ? 'bg-indigo-500' : reversed ? 'bg-amber-400' : 'bg-gray-300 dark:bg-gray-600'
        }`}
        aria-hidden
      />
      <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
        <span className={`font-medium ${reversed ? 'text-amber-700 dark:text-amber-300' : 'text-gray-700 dark:text-gray-200'}`}>
          {movementWords(a) || 'version'}
        </span>
        {/* The rung it moved, as a move — "leaning → likely" is the direction
            of travel, where one chip is only a position. */}
        {e.strengthFrom ? (
          <span className="text-gray-400" data-testid="answer-strength-move">
            <Strength value={e.strengthFrom} /> → <Strength value={a.strength} />
          </span>
        ) : (
          <Strength value={a.strength} />
        )}
        {e.current && <span className="text-indigo-600 dark:text-indigo-300">standing now</span>}
        {e.dismissed && (
          <span className="text-gray-400" data-testid={`answer-dismissed-${a.id}`}>
            dismissed{a.dismissed_reason ? `: “${a.dismissed_reason}”` : ''}
          </span>
        )}
        <span className="ml-auto text-gray-400" title={when(a.created_at)}>
          {a.based_on_lookups_done > 0 && `on ${a.based_on_lookups_done} lookup${a.based_on_lookups_done === 1 ? '' : 's'}`}
        </span>
      </div>

      {/* THE ROOM'S DIRECTION CHANGING. A version answering a different
          question looked identical to one answering the same one. */}
      {e.questionNow && (
        <div className="mt-1 text-xs text-indigo-700 dark:text-indigo-300" data-testid="answer-question-changed">
          now answering: {e.questionNow}
        </div>
      )}

      <div className={`mt-1 text-xs leading-snug ${e.dismissed ? 'text-gray-400 line-through' : 'text-gray-800 dark:text-gray-100'}`}>
        {a.stance}
      </div>
      {a.moved_by && <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{a.moved_by}</div>}

      {(e.diff.added.length > 0 || gone.length > 0) && (
        <ul className="mt-1 flex flex-col gap-0.5 text-xs" data-testid={`answer-diff-${a.id}`}>
          {e.diff.added.map(t => (
            <li key={`+${t}`} className="text-emerald-700 dark:text-emerald-400">
              + {t}
            </li>
          ))}
          {gone.map(g => (
            <li key={`-${g.text}`} className="text-gray-500 dark:text-gray-400">
              − <span className="line-through">{g.text}</span>
              {g.why && <span> - {g.why}</span>}
            </li>
          ))}
        </ul>
      )}

      {!e.current && (
        <button
          type="button"
          onClick={() => onUse(a.id)}
          disabled={busy}
          className="mt-1 text-xs text-indigo-600 hover:underline disabled:opacity-50 dark:text-indigo-300"
        >
          {e.dismissed ? 'bring this back' : 'use this answer'}
        </button>
      )}
    </li>
  );
}
